import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { MailWarning, Send } from "lucide-react";
import { useAuthStore } from "@/store/auth.store";
import { resendVerificationEmailApi } from "@/services/auth.service";

export function EmailVerificationModal() {
  const [open, setOpen] = useState(false);
  const [resendStatus, setResendStatus] = useState<"idle" | "loading" | "sent" | "error">("idle");
  const logout = useAuthStore((s) => s.logout);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("email-not-verified", handler);
    return () => window.removeEventListener("email-not-verified", handler);
  }, []);

  function handleConfirm() {
    setOpen(false);
    logout();
  }

  async function handleResend() {
    if (!user?.email || resendStatus === "loading" || resendStatus === "sent") return;
    setResendStatus("loading");
    try {
      // Le bouton promet un e-mail d'*activation* : on appelle l'endpoint dédié
      // (`/v1/auth/email/verification-notification`), pas `forgot-password` qui envoie un lien de
      // réinitialisation de mot de passe et renvoyait un 500 sur ces comptes (SUCCESCHOOL-FRONTEND-V2-90).
      await resendVerificationEmailApi(user.email);
      setResendStatus("sent");
    } catch {
      setResendStatus("error");
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <MailWarning className="w-5 h-5 text-amber-600" />
            </div>
            <AlertDialogTitle className="text-lg font-black text-[#0F172A]">
              Compte non activé
            </AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-sm text-gray-600 leading-relaxed">
            Vous n'avez pas finalisé votre inscription. Veuillez vérifier votre boîte mail et
            cliquer sur le lien de confirmation que nous vous avons envoyé pour activer votre compte
            et accéder à votre espace.
          </AlertDialogDescription>
          {resendStatus === "sent" && (
            <p className="text-sm text-green-600 font-medium mt-2">
              Email de confirmation renvoyé. Vérifiez votre boîte mail.
            </p>
          )}
          {resendStatus === "error" && (
            <p className="text-sm text-red-500 mt-2">
              Une erreur est survenue. Veuillez réessayer.
            </p>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-col">
          <AlertDialogAction
            onClick={handleConfirm}
            className="bg-[#003366] hover:bg-[#004a8f] text-white font-bold w-full"
          >
            OK, aller à la connexion
          </AlertDialogAction>
          <Button
            variant="outline"
            onClick={handleResend}
            disabled={resendStatus === "loading" || resendStatus === "sent"}
            className="w-full border-[#003366] text-[#003366] hover:bg-[#003366]/5 font-semibold"
          >
            <Send className="w-4 h-4 mr-2" />
            {resendStatus === "loading"
              ? "Envoi en cours..."
              : resendStatus === "sent"
                ? "Email envoyé"
                : "Renvoyer l'email d'activation"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
